import React, { useRef, Fragment } from 'react';
import { motion, useInView } from 'framer-motion';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/83gAX3TsGvnbvH5ew774Zt/Metro_Logo_HQ26.png';
/* ------------------------------------------------------------------ */
/*  FLYER CANVAS — print file frame with crop marks                    */
/* ------------------------------------------------------------------ */
function FlyerCanvas({
  aspect,
  bg,
  textColor = '#0E0E12',
  label,
  children
}: {
  aspect: string;
  bg: string;
  textColor?: string;
  label: string;
  children: React.ReactNode;
}) {
  return <div className="flex flex-col gap-2" data-id="element-612">
      <div className="relative w-full overflow-hidden" style={{
      aspectRatio: aspect,
      background: bg,
      color: textColor,
      boxShadow: '0 8px 28px rgba(0,0,0,0.12)'
    }} data-id="element-613">
        {/* crop marks */}
        {['top-2 left-2', 'top-2 right-2', 'bottom-2 left-2', 'bottom-2 right-2'].map((p, i) => <div key={i} className={`absolute ${p} w-3 h-3 opacity-30 pointer-events-none`} style={{
        color: textColor
      }} data-id="element-614">
            <svg viewBox="0 0 12 12" fill="none" data-id="element-615">
              <line x1="6" y1="0" x2="6" y2="12" stroke="currentColor" strokeWidth="0.6" data-id="element-616" />
              <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="0.6" data-id="element-617" />
            </svg>
          </div>)}
        <div className="w-full h-full" data-id="element-618">{children}</div>
      </div>
      <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-[0.3em] text-center" data-id="element-619">
        {label}
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/*  FLYER BLOCK — shows front + back side by side + metadata           */
/* ------------------------------------------------------------------ */
function FlyerBlock({
  number,
  title,
  format,
  basedOn,
  caption,
  inks,
  children
}: {
  number: string;
  title: string;
  format: string;
  basedOn: string;
  caption: string;
  inks: string[];
  children: React.ReactNode;
}) {
  return <div className="border border-[var(--divider)] rounded-sm bg-white overflow-hidden mb-12" data-id="element-620">
      {/* Header */}
      <div className="grid grid-cols-12 gap-4 p-5 border-b border-[var(--divider)] items-start" data-id="element-621">
        <div className="col-span-12 md:col-span-5" data-id="element-622">
          <div className="font-mono text-[10px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-623">
            {number}
          </div>
          <div className="font-space font-bold text-2xl tracking-tight leading-tight" data-id="element-624">
            {title}
          </div>
          <div className="text-[11px] text-[var(--text-muted)] leading-relaxed mt-2 max-w-md" data-id="element-625">
            {caption}
          </div>
        </div>
        <div className="col-span-6 md:col-span-3" data-id="element-626">
          <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-[0.3em] mb-1.5" data-id="element-627">
            FORMATO
          </div>
          <div className="font-space font-bold text-sm leading-tight" data-id="element-628">
            {format}
          </div>
        </div>
        <div className="col-span-6 md:col-span-2" data-id="element-629">
          <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-[0.3em] mb-1.5" data-id="element-630">
            BASADO EN
          </div>
          <div className="font-space font-bold text-sm leading-tight" data-id="element-631">
            {basedOn}
          </div>
        </div>
        <div className="col-span-12 md:col-span-2" data-id="element-632">
          <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-[0.3em] mb-1.5" data-id="element-633">
            TINTAS
          </div>
          <div className="flex items-center gap-1.5" data-id="element-634">
            {inks.map(c => <span key={c} className="w-3 h-3 rounded-full border border-black/15" style={{
            background: c
          }} data-id="element-635" />)}
          </div>
        </div>
      </div>
      {/* Faces */}
      <div className="p-8 md:p-10" style={{
      background: 'repeating-linear-gradient(45deg, #F4F5F7 0 10px, #EDEEF1 10px 20px)'
    }} data-id="element-636">
        {children}
      </div>
    </div>;
}
/* ================================================================== */
/*  FLYER 01 — SERVICE MENU FLYER (DL 99×210mm)                        */
/*  Inspired by Poster 11.1.2 "Service Menu"                           */
/* ================================================================== */
const DL_ASPECT = '99 / 210';
function Flyer01Front() {
  const services = [{
    n: '01',
    c: '#1A2BFF',
    name: 'Brand',
    price: '€1,200',
    desc: 'Identidad de A→Z'
  }, {
    n: '02',
    c: '#E8C547',
    name: 'Website',
    price: '€390',
    desc: 'One-page · 72h'
  }, {
    n: '03',
    c: '#F26A35',
    name: 'Content',
    price: '€350',
    desc: 'Sesión + edición'
  }, {
    n: '04',
    c: '#2A8E47',
    name: 'Performance',
    price: '€350/mo',
    desc: 'SEO + analytics'
  }, {
    n: '05',
    c: '#F4A523',
    name: 'Social',
    price: '€450/mo',
    desc: 'Reels + community'
  }, {
    n: '06',
    c: '#0E0E12',
    name: '360°',
    price: 'Custom',
    desc: 'Todo el sistema'
  }];
  return <FlyerCanvas aspect={DL_ASPECT} bg="#EDEEF1" label="FRONT · DL 99×210 MM" data-id="element-637">
      <div className="w-full h-full p-5 flex flex-col" data-id="element-638">
        {/* eyebrow */}
        <div className="flex items-center justify-between font-mono text-[7px] tracking-[0.35em] text-[#0E0E12]/65 pb-2 border-b border-[#0E0E12]" data-id="element-639">
          <span data-id="element-640">MENU · 2026</span>
          <span data-id="element-641">BCN · 01/03</span>
        </div>
        {/* headline */}
        <div className="mt-5" data-id="element-642">
          <div className="font-space font-extrabold leading-[0.85] tracking-[-0.04em]" style={{
          fontSize: 36
        }} data-id="element-643">
            One team.
            <br data-id="element-644" />
            Every craft.
          </div>
          <div className="text-[8px] text-[#0E0E12]/60 mt-2 leading-snug" data-id="element-645">
            Seis líneas. Una agencia. Pídelas sueltas o todas en un sistema.
          </div>
        </div>
        {/* menu list */}
        <div className="flex-1 min-h-0 flex flex-col justify-center mt-4" data-id="element-646">
          {services.map(s => <div key={s.n} className="grid items-center border-b border-[#0E0E12]/70 py-2" style={{
          gridTemplateColumns: '14px 1fr auto',
          gap: 8
        }} data-id="element-647">
              <span className="w-2 h-2 rounded-full" style={{
            background: s.c
          }} data-id="element-648" />
              <div data-id="element-649">
                <div className="font-space font-bold tracking-tight leading-none text-[14px]" data-id="element-650">
                  {s.name}
                </div>
                <div className="font-mono text-[6px] tracking-[0.25em] text-[#0E0E12]/55 mt-1" data-id="element-651">
                  {s.n} · {s.desc.toUpperCase()}
                </div>
              </div>
              <div className="text-right" data-id="element-652">
                <div className="font-mono text-[5.5px] tracking-[0.3em] text-[#0E0E12]/55" data-id="element-653">
                  FROM
                </div>
                <div className="font-space font-extrabold tracking-tight leading-none mt-0.5" style={{
              fontSize: 13,
              color: '#1A2BFF'
            }} data-id="element-654">
                  {s.price}
                </div>
              </div>
            </div>)}
        </div>
        {/* footer */}
        <div className="flex items-end justify-between pt-3 border-t-2 border-[#0E0E12] mt-3" data-id="element-655">
          <div data-id="element-656">
            <div className="font-mono text-[6px] tracking-[0.3em] text-[#0E0E12]/65" data-id="element-657">
              METROAGENCY.CO
            </div>
            <div className="font-space font-extrabold italic text-[16px] tracking-tight leading-none mt-1" data-id="element-658">
              start a project →
            </div>
          </div>
          <img src={METRO_LOGO_URL} alt="" style={{
          height: 22
        }} data-id="element-659" />
        </div>
      </div>
    </FlyerCanvas>;
}
function Flyer01Back() {
  return <FlyerCanvas aspect={DL_ASPECT} bg="#0E0E12" textColor="#FFFFFF" label="BACK · DL 99×210 MM" data-id="element-660">
      <div className="w-full h-full p-5 flex flex-col text-white" data-id="element-661">
        {/* eyebrow */}
        <div className="flex items-center justify-between font-mono text-[7px] tracking-[0.35em] text-white/55 pb-2 border-b border-white/30" data-id="element-662">
          <span data-id="element-663">HOW WE WORK</span>
          <span data-id="element-664">02/02</span>
        </div>
        {/* manifesto */}
        <div className="mt-5" data-id="element-665">
          <div className="font-space font-extrabold italic leading-[0.85] tracking-[-0.04em]" style={{
          fontSize: 34
        }} data-id="element-666">
            donde
            <br data-id="element-667" />
            <span style={{
            color: '#F26A35'
          }} data-id="element-668">
              las ideas
            </span>
            <br data-id="element-669" />
            conectan.
          </div>
        </div>
        {/* three steps */}
        <div className="flex-1 min-h-0 flex flex-col justify-center gap-3 mt-5" data-id="element-670">
          {[{
          n: '01',
          t: 'Brief.',
          d: 'Una llamada de 30 min. Sin fee. Nos cuentas qué duele.'
        }, {
          n: '02',
          t: 'Proposal.',
          d: 'En 48h, scope + precio fijo + calendario. Sin ambigüedad.'
        }, {
          n: '03',
          t: 'Ship.',
          d: 'Producción semanal. Revisiones acotadas. Entrega cerrada.'
        }].map(s => <div key={s.n} className="border-l-2 border-[#F26A35] pl-3" data-id="element-671">
              <div className="font-mono text-[6px] tracking-[0.3em] text-[#F26A35]" data-id="element-672">
                {s.n}
              </div>
              <div className="font-space font-extrabold text-[15px] tracking-tight leading-none mt-1" data-id="element-673">
                {s.t}
              </div>
              <div className="text-[8px] text-white/70 leading-snug mt-1" data-id="element-674">
                {s.d}
              </div>
            </div>)}
        </div>
        {/* QR + contact */}
        <div className="border-t border-white/30 pt-3 flex items-end gap-3" data-id="element-675">
          {/* QR placeholder */}
          <div className="grid grid-cols-7 gap-px bg-white p-1.5" style={{
          width: 56,
          height: 56
        }} data-id="element-676">
            {Array.from({
            length: 49
          }).map((_, i) => {
            const seed = (i * 13 + 7) % 5;
            return <div key={i} style={{
              background: seed > 2 ? '#0E0E12' : 'transparent'
            }} data-id="element-677" />;
          })}
          </div>
          <div className="flex-1" data-id="element-678">
            <div className="font-mono text-[6px] tracking-[0.3em] text-white/55" data-id="element-679">
              ESCANEA · BOOK CALL
            </div>
            <div className="font-mono text-[7.5px] tracking-[0.2em] text-white mt-1" data-id="element-680">
              metroagency.co
            </div>
            <div className="font-mono text-[7.5px] tracking-[0.2em] text-white" data-id="element-681">
              hola@metroagency.co
            </div>
            <div className="font-mono text-[7px] tracking-[0.2em] text-white/55 mt-1" data-id="element-682">
              +34 600 000 000
            </div>
          </div>
        </div>
      </div>
    </FlyerCanvas>;
}
/* ================================================================== */
/*  FLYER 02 — FIVE PILLARS FLYER (A5 148×210mm)                       */
/*  Inspired by Poster 11.1.3 "Five Pillars"                           */
/* ================================================================== */
const A5_ASPECT = '148 / 210';
const PILLARS = [{
  n: '01',
  t: 'Brand',
  p: '€1,200',
  bg: '#1A2BFF',
  fg: '#FFFFFF',
  deliverable: 'Naming · Logo · Sistema'
}, {
  n: '02',
  t: 'Web',
  p: '€390',
  bg: '#E8C547',
  fg: '#0E0E12',
  deliverable: 'One-page · Hosting · SEO'
}, {
  n: '03',
  t: 'Content',
  p: '€350',
  bg: '#F26A35',
  fg: '#FFFFFF',
  deliverable: 'Foto · Vídeo · Edición'
}, {
  n: '04',
  t: 'Perform',
  p: '€350/mo',
  bg: '#2A8E47',
  fg: '#FFFFFF',
  deliverable: 'SEO · Ads · Analytics'
}];
function Flyer02Front() {
  return <FlyerCanvas aspect={A5_ASPECT} bg="#0E0E12" textColor="#FFFFFF" label="FRONT · A5 148×210 MM" data-id="element-683">
      <div className="w-full h-full p-6 flex flex-col text-white" data-id="element-684">
        {/* eyebrow */}
        <div className="flex items-center justify-between font-mono text-[8px] tracking-[0.4em] text-white/55 pb-2 border-b border-white/25" data-id="element-685">
          <span data-id="element-686">PILLARS</span>
          <span data-id="element-687">METRO · 26</span>
        </div>
        {/* headline */}
        <div className="mt-6" data-id="element-688">
          <div className="font-space font-extrabold leading-[0.85] tracking-[-0.03em]" style={{
          fontSize: 50
        }} data-id="element-689">
            Strategy
            <br data-id="element-690" />
            <span style={{
            color: '#1A2BFF'
          }} data-id="element-691">
              to screen.
            </span>
          </div>
          <div className="text-[10px] text-white/65 mt-2 leading-snug max-w-[80%]" data-id="element-692">
            No silos. No passing the buck. Cinco pilares, un solo equipo —
            llévatelos sueltos o como sistema.
          </div>
        </div>
        {/* 2x2 grid + wide 360 */}
        <div className="flex-1 min-h-0 grid grid-cols-2 gap-2 mt-6" data-id="element-693">
          {PILLARS.map(p => <div key={p.n} className="p-3 flex flex-col justify-between min-w-0" style={{
          background: p.bg,
          color: p.fg
        }} data-id="element-694">
              <div className="font-mono font-bold" style={{
            fontSize: 8,
            letterSpacing: '0.25em',
            opacity: 0.8
          }} data-id="element-695">
                {p.n}
              </div>
              <div data-id="element-696">
                <div className="font-space font-extrabold leading-none tracking-tight" style={{
              fontSize: 26
            }} data-id="element-697">
                  {p.t}
                </div>
                <div className="font-mono font-bold mt-2" style={{
              fontSize: 7,
              letterSpacing: '0.22em',
              opacity: 0.9
            }} data-id="element-698">
                  FROM {p.p}
                </div>
              </div>
            </div>)}
          {/* wide 360 */}
          <div className="col-span-2 p-3 flex items-center justify-between min-w-0" style={{
          background: '#EDEEF1',
          color: '#0E0E12'
        }} data-id="element-699">
            <div data-id="element-700">
              <div className="font-mono font-bold" style={{
              fontSize: 8,
              letterSpacing: '0.25em',
              opacity: 0.7
            }} data-id="element-701">
                05
              </div>
              <div className="font-space font-extrabold leading-none tracking-tight mt-1" style={{
              fontSize: 24
            }} data-id="element-702">
                360° Marketing
              </div>
            </div>
            <div className="text-right" data-id="element-703">
              <div className="font-mono text-[6.5px] tracking-[0.25em] text-[#0E0E12]/60" data-id="element-704">
                ALL OF THE ABOVE
              </div>
              <div className="font-mono font-bold mt-1" style={{
              fontSize: 10,
              letterSpacing: '0.22em',
              color: '#1A2BFF'
            }} data-id="element-705">
                CUSTOM
              </div>
            </div>
          </div>
        </div>
        {/* footer */}
        <div className="flex items-end justify-between pt-3 border-t border-white/25 mt-4" data-id="element-706">
          <div data-id="element-707">
            <div className="font-mono text-[7px] tracking-[0.3em] text-white/55" data-id="element-708">
              METROAGENCY.CO
            </div>
            <div className="font-space font-extrabold italic text-[18px] tracking-tight leading-none mt-1" data-id="element-709">
              start a project →
            </div>
          </div>
          <img src={METRO_LOGO_URL} alt="" style={{
          height: 26,
          filter: 'brightness(0) invert(1)'
        }} data-id="element-710" />
        </div>
      </div>
    </FlyerCanvas>;
}
function Flyer02Back() {
  return <FlyerCanvas aspect={A5_ASPECT} bg="#FAF4E7" label="BACK · A5 148×210 MM" data-id="element-711">
      <div className="w-full h-full p-6 flex flex-col" data-id="element-712">
        {/* eyebrow */}
        <div className="flex items-center justify-between font-mono text-[8px] tracking-[0.4em] text-[#0E0E12]/60 pb-2 border-b border-[#0E0E12]/40" data-id="element-713">
          <span data-id="element-714">WHAT YOU GET</span>
          <span data-id="element-715">EACH PILLAR · UNFOLDED</span>
        </div>
        {/* headline */}
        <div className="mt-5" data-id="element-716">
          <div className="font-space font-extrabold leading-[0.88] tracking-[-0.03em]" style={{
          fontSize: 28
        }} data-id="element-717">
            Cada pilar,
            <br data-id="element-718" />
            <span style={{
            color: '#1A2BFF'
          }} data-id="element-719">
              desplegado.
            </span>
          </div>
        </div>
        {/* detailed pillars */}
        <div className="flex-1 min-h-0 flex flex-col justify-center gap-2.5 mt-5" data-id="element-720">
          {PILLARS.map(p => <div key={p.n} className="border-t border-[#0E0E12]/40 pt-2 grid items-center" style={{
          gridTemplateColumns: '40px 1fr auto',
          gap: 10
        }} data-id="element-721">
              <div className="flex items-center justify-center font-space font-extrabold" style={{
            width: 38,
            height: 38,
            background: p.bg,
            color: p.fg,
            fontSize: 16,
            letterSpacing: '-0.05em'
          }} data-id="element-722">
                {p.t.slice(0, 1)}
              </div>
              <div className="min-w-0" data-id="element-723">
                <div className="font-space font-bold text-[15px] tracking-tight leading-none" data-id="element-724">
                  {p.t}
                </div>
                <div className="font-mono text-[7px] tracking-[0.2em] text-[#0E0E12]/65 mt-1.5" data-id="element-725">
                  {p.deliverable.toUpperCase()}
                </div>
              </div>
              <div className="text-right" data-id="element-726">
                <div className="font-mono text-[6px] tracking-[0.3em] text-[#0E0E12]/55" data-id="element-727">
                  FROM
                </div>
                <div className="font-space font-extrabold leading-none mt-0.5" style={{
              fontSize: 14,
              color: '#1A2BFF'
            }} data-id="element-728">
                  {p.p}
                </div>
              </div>
            </div>)}
          {/* 360 row */}
          <div className="grid items-center pt-2 border-t-2 border-[#0E0E12]" style={{
          gridTemplateColumns: '40px 1fr auto',
          gap: 10
        }} data-id="element-729">
            <div className="flex items-center justify-center font-space font-extrabold text-white" style={{
            width: 38,
            height: 38,
            background: '#0E0E12',
            fontSize: 18
          }} data-id="element-730">
              ∞
            </div>
            <div data-id="element-731">
              <div className="font-space font-bold text-[15px] tracking-tight leading-none" data-id="element-732">
                360° System
              </div>
              <div className="font-mono text-[7px] tracking-[0.2em] text-[#0E0E12]/65 mt-1.5" data-id="element-733">
                LOS CINCO · ORQUESTADOS
              </div>
            </div>
            <div className="text-right" data-id="element-734">
              <div className="font-mono text-[6px] tracking-[0.3em] text-[#0E0E12]/55" data-id="element-735">
                FROM
              </div>
              <div className="font-space font-extrabold leading-none mt-0.5" style={{
              fontSize: 14,
              color: '#1A2BFF'
            }} data-id="element-736">
                CUSTOM
              </div>
            </div>
          </div>
        </div>
        {/* footer with QR */}
        <div className="border-t-2 border-[#0E0E12] pt-3 mt-4 flex items-end justify-between gap-3" data-id="element-737">
          <div data-id="element-738">
            <div className="font-mono text-[7px] tracking-[0.3em] text-[#0E0E12]/60" data-id="element-739">
              BOOK A 30 MIN BRIEF
            </div>
            <div className="font-space font-extrabold italic text-[18px] tracking-tight leading-none mt-1" data-id="element-740">
              metroagency.co/brief
            </div>
            <div className="font-mono text-[7px] tracking-[0.25em] text-[#0E0E12]/65 mt-1" data-id="element-741">
              hola@metroagency.co · +34 600 000 000
            </div>
          </div>
          <div className="grid grid-cols-7 gap-px bg-[#0E0E12] p-1" style={{
          width: 56,
          height: 56
        }} data-id="element-742">
            {Array.from({
            length: 49
          }).map((_, i) => {
            const seed = (i * 17 + 3) % 5;
            return <div key={i} style={{
              background: seed > 2 ? '#FAF4E7' : 'transparent'
            }} data-id="element-743" />;
          })}
          </div>
        </div>
      </div>
    </FlyerCanvas>;
}
/* ================================================================== */
/*  FLYER 03 — MARKETING 360° FLYER (Square 200×200mm)                 */
/*  Inspired by Poster 11.2.5 "Marketing 360°"                         */
/* ================================================================== */
const SQ_ASPECT = '1 / 1';
function NetworkSVG({
  small = false
}: {
  small?: boolean;
}) {
  return <svg viewBox="0 0 280 360" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full" data-id="element-744">
      <path d="M -10 60 L 80 60 L 140 120 L 290 120" stroke="#1A2BFF" strokeWidth={small ? 4 : 6} fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-745" />
      <path d="M -10 130 L 60 130 L 100 90 L 200 90 L 240 130 L 290 130" stroke="#E8C547" strokeWidth={small ? 4 : 6} fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-746" />
      <path d="M -10 200 L 100 200 L 160 260 L 290 260" stroke="#F26A35" strokeWidth={small ? 4 : 6} fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-747" />
      <path d="M -10 290 L 80 290 L 130 340 L 290 340" stroke="#2A8E47" strokeWidth={small ? 4 : 6} fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-748" />
      <path d="M 60 -10 L 60 290 L 130 340" stroke="#F4A523" strokeWidth={small ? 4 : 6} fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-749" />
      {[[80, 60], [140, 120], [60, 130], [100, 90], [200, 90], [240, 130], [100, 200], [160, 260], [80, 290], [130, 340], [60, 60]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={small ? 4 : 6} fill="#FFFFFF" stroke="#0E0E12" strokeWidth="2" data-id="element-750" />)}
    </svg>;
}
function Flyer03Front() {
  return <FlyerCanvas aspect={SQ_ASPECT} bg="#EDEEF1" label="FRONT · SQUARE 200×200 MM" data-id="element-751">
      <div className="w-full h-full p-6 flex flex-col relative" data-id="element-752">
        {/* eyebrow */}
        <div className="flex items-center justify-between font-mono text-[8px] tracking-[0.4em] text-[#0E0E12]/60 pb-2 border-b border-[#0E0E12] relative z-10" data-id="element-753">
          <span data-id="element-754">360°</span>
          <span data-id="element-755">BCN · NETWORK FLYER</span>
        </div>
        {/* Map area */}
        <div className="flex-1 min-h-0 relative mt-3" data-id="element-756">
          <NetworkSVG data-id="element-757" />
          {/* Black overlay band */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2" data-id="element-758">
            <div className="bg-[#0E0E12] text-white mx-3 px-4 py-4" data-id="element-759">
              <div className="font-mono text-[7px] tracking-[0.4em] text-white/65 mb-2" data-id="element-760">
                MARKETING 360°
              </div>
              <div className="font-space font-extrabold leading-[0.85] tracking-[-0.03em]" style={{
              fontSize: 44
            }} data-id="element-761">
                Not a menu.
                <br data-id="element-762" />A{' '}
                <span style={{
                color: '#1A2BFF'
              }} data-id="element-763">
                  system.
                </span>
              </div>
              <div className="text-[10px] text-white/85 leading-snug mt-3" data-id="element-764">
                Strategy. Creative. Production. One team — cinco líneas, una
                sola red.
              </div>
            </div>
          </div>
        </div>
        {/* footer */}
        <div className="flex items-end justify-between pt-3 border-t border-[#0E0E12]/40 relative z-10" data-id="element-765">
          <div data-id="element-766">
            <div className="font-mono text-[7px] tracking-[0.3em] text-[#0E0E12]/65" data-id="element-767">
              METROAGENCY.CO/360
            </div>
            <div className="font-space font-extrabold italic text-[18px] tracking-tight leading-none mt-1" data-id="element-768">
              ride the system →
            </div>
          </div>
          <img src={METRO_LOGO_URL} alt="" style={{
          height: 26
        }} data-id="element-769" />
        </div>
      </div>
    </FlyerCanvas>;
}
function Flyer03Back() {
  const lines = [{
    c: '#1A2BFF',
    l: 'L-1',
    name: 'BRAND',
    stops: ['Naming', 'Logo', 'Sistema', 'Guidelines']
  }, {
    c: '#E8C547',
    l: 'L-2',
    name: 'WEB',
    stops: ['One-page', 'Multi-page', 'Hosting', 'SEO']
  }, {
    c: '#F26A35',
    l: 'L-3',
    name: 'CONTENT',
    stops: ['Foto', 'Vídeo', 'Edición', 'Reels']
  }, {
    c: '#2A8E47',
    l: 'L-4',
    name: 'PERFORM',
    stops: ['SEO', 'Ads', 'Analytics', 'CRO']
  }, {
    c: '#F4A523',
    l: 'L-5',
    name: 'SOCIAL',
    stops: ['Community', 'Calendario', 'Reels', 'Ads']
  }];
  return <FlyerCanvas aspect={SQ_ASPECT} bg="#FFFFFF" label="BACK · SQUARE 200×200 MM" data-id="element-770">
      <div className="w-full h-full p-6 flex flex-col" data-id="element-771">
        {/* eyebrow */}
        <div className="flex items-center justify-between font-mono text-[8px] tracking-[0.4em] text-[#0E0E12]/60 pb-2 border-b border-[#0E0E12]" data-id="element-772">
          <span data-id="element-773">LEGEND</span>
          <span data-id="element-774">FIVE LINES · ONE NETWORK</span>
        </div>
        {/* headline */}
        <div className="mt-4 flex items-baseline justify-between" data-id="element-775">
          <div className="font-space font-extrabold leading-[0.88] tracking-[-0.03em]" style={{
          fontSize: 26
        }} data-id="element-776">
            La red,
            <br data-id="element-777" />
            <span style={{
            color: '#1A2BFF'
          }} data-id="element-778">
              línea por línea.
            </span>
          </div>
          <div className="font-mono text-[7px] tracking-[0.3em] text-[#0E0E12]/55 text-right" data-id="element-779">
            ESCALA
            <br data-id="element-780" />
            1:1
          </div>
        </div>
        {/* lines list */}
        <div className="flex-1 min-h-0 flex flex-col justify-center gap-2.5 mt-5" data-id="element-781">
          {lines.map(line => <div key={line.l} className="grid items-center gap-3" style={{
          gridTemplateColumns: '50px 1fr'
        }} data-id="element-782">
              {/* line bullet */}
              <div className="flex items-center gap-1.5" data-id="element-783">
                <div className="font-space font-bold text-white text-[10px] rounded-full flex items-center justify-center" style={{
              width: 22,
              height: 22,
              background: line.c,
              color: line.c === '#E8C547' || line.c === '#F4A523' ? '#0E0E12' : '#FFFFFF'
            }} data-id="element-784">
                  {line.l}
                </div>
              </div>
              {/* stops */}
              <div className="min-w-0" data-id="element-785">
                <div className="flex items-baseline gap-2" data-id="element-786">
                  <span className="font-space font-extrabold tracking-tight leading-none" style={{
                fontSize: 13
              }} data-id="element-787">
                    {line.name}
                  </span>
                  <span className="flex-1 h-px border-t border-dashed border-[#0E0E12]/30" data-id="element-788" />
                </div>
                <div className="flex items-center gap-1.5 mt-1.5" data-id="element-789">
                  {line.stops.map((s, i) => <Fragment key={s} data-id="element-790">
                      <div className="flex items-center gap-1" data-id="element-791">
                        <span className="w-1.5 h-1.5 rounded-full border-2 bg-white" style={{
                    borderColor: line.c
                  }} data-id="element-792" />
                        <span className="font-mono text-[6.5px] tracking-[0.2em] text-[#0E0E12]/75" data-id="element-793">
                          {s.toUpperCase()}
                        </span>
                      </div>
                      {i < line.stops.length - 1 && <span className="h-0.5 w-2" style={{
                  background: line.c,
                  opacity: 0.6
                }} data-id="element-794" />}
                    </Fragment>)}
                </div>
              </div>
            </div>)}
        </div>
        {/* footer */}
        <div className="border-t-2 border-[#0E0E12] pt-3 mt-3 flex items-end justify-between" data-id="element-795">
          <div data-id="element-796">
            <div className="font-mono text-[7px] tracking-[0.3em] text-[#0E0E12]/60" data-id="element-797">
              CONECTA CON EL SISTEMA
            </div>
            <div className="font-mono text-[8px] tracking-[0.2em] text-[#0E0E12] mt-1" data-id="element-798">
              metroagency.co · hola@metroagency.co
            </div>
          </div>
          <div className="font-space font-extrabold italic tracking-tight" style={{
          fontSize: 14,
          color: '#1A2BFF'
        }} data-id="element-799">
            transbordo gratuito.
          </div>
        </div>
      </div>
    </FlyerCanvas>;
}
/* ================================================================== */
/*  MAIN SECTION                                                       */
/* ================================================================== */
export function FlyersSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="flyers" ref={ref} className="min-h-screen px-8 py-24" data-id="element-800">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-801">
        {/* Section header */}
        <div className="mb-12 max-w-3xl" data-id="element-802">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-803">
            13 / FLYERS · PRINT FILES
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-804">
            La red, en mano.
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 font-helvetica leading-relaxed" data-id="element-805">
            Tres flyers de dos caras, derivados de los pósters 11.1.2, 11.1.3 y
            11.2.5. Cada uno cuenta una versión distinta de la misma historia —
            el menú de servicios, los cinco pilares y el sistema 360°. Pensados
            para entregar en eventos, dejar en estudios aliados o repartir en
            networking de Barcelona.
          </p>
        </div>

        {/* Production strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16" data-id="element-806">
          {[{
          t: 'Formatos',
          v: 'DL · A5 · Square',
          d: '99×210 · 148×210 · 200×200'
        }, {
          t: 'Papel',
          v: 'Coated mate 250gr',
          d: 'Sin laminado · barniz selectivo'
        }, {
          t: 'Impresión',
          v: 'Offset · 4/4 tintas',
          d: 'Pantone + CMYK para fondos planos'
        }, {
          t: 'Tirada',
          v: '500 / 1.000 / 2.500',
          d: 'Por trimestre, según evento'
        }].map(c => <div key={c.t} className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)]" data-id="element-807">
              <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-808">
                {c.t.toUpperCase()}
              </div>
              <div className="font-space font-bold text-base leading-tight mb-1" data-id="element-809">
                {c.v}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-810">
                {c.d}
              </div>
            </div>)}
        </div>

        {/* Flyer 01 */}
        <FlyerBlock number="13.1" title="Service Menu Flyer." format="DL · 99×210MM" basedOn="POSTER 11.1.2" caption="El menú de los seis servicios en formato vertical alto, listo para colocar en mostradores de eventos. Frente: lista completa con precios. Reverso: tres pasos del workflow + QR a reservar brief." inks={['#1A2BFF', '#F26A35', '#E8C547', '#0E0E12']} data-id="element-811">
          <div className="grid grid-cols-2 gap-6 max-w-3xl mx-auto" data-id="element-812">
            <Flyer01Front data-id="element-813" />
            <Flyer01Back data-id="element-814" />
          </div>
        </FlyerBlock>

        {/* Flyer 02 */}
        <FlyerBlock number="13.2" title="Five Pillars Flyer." format="A5 · 148×210MM" basedOn="POSTER 11.1.3" caption="A5 portrait con la rejilla 2×2 de los cuatro pilares + bloque 360° ancho. Frente: el grid de marca, web, content y performance. Reverso: cada pilar desplegado con entregables específicos." inks={['#1A2BFF', '#E8C547', '#F26A35', '#2A8E47', '#0E0E12']} data-id="element-815">
          <div className="grid grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-816">
            <Flyer02Front data-id="element-817" />
            <Flyer02Back data-id="element-818" />
          </div>
        </FlyerBlock>

        {/* Flyer 03 */}
        <FlyerBlock number="13.3" title="Marketing 360° Flyer." format="SQUARE · 200×200MM" basedOn="POSTER 11.2.5" caption="Cuadrado de gran formato. Frente: la red Metro con la banda negra 'Not a menu. A system.' Reverso: leyenda completa — cada línea de color = un servicio, cada parada = una pieza entregable." inks={['#1A2BFF', '#E8C547', '#F26A35', '#2A8E47', '#F4A523']} data-id="element-819">
          <div className="grid grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-820">
            <Flyer03Front data-id="element-821" />
            <Flyer03Back data-id="element-822" />
          </div>
        </FlyerBlock>

        {/* Closing notes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12" data-id="element-823">
          <div className="border border-[var(--divider)] rounded-sm p-5 bg-[var(--bg-surface)]" data-id="element-824">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-825">
              SISTEMA
            </div>
            <div className="font-space font-bold text-lg leading-tight mb-2" data-id="element-826">
              Mismo lenguaje, tres lecturas.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-827">
              Los tres flyers comparten paleta, tipografía y vocabulario
              transit. Lo que cambia es la profundidad — del listado básico al
              sistema completo, según el lector y el evento.
            </div>
          </div>
          <div className="border border-[var(--divider)] rounded-sm p-5 bg-[var(--bg-surface)]" data-id="element-828">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-829">
              DISTRIBUCIÓN
            </div>
            <div className="font-space font-bold text-lg leading-tight mb-2" data-id="element-830">
              Mano a mano, no buzón.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-831">
              Pensados para entrega presencial — networking, ferias de diseño,
              estudios aliados, eventos de OFFF y Madison. Nunca buzoneo masivo.
            </div>
          </div>
          <div className="border border-[var(--divider)] rounded-sm p-5 bg-[var(--text-ink)] text-white" data-id="element-832">
            <div className="font-mono text-[10px] tracking-widest opacity-70 mb-2" data-id="element-833">
              CONVERSIÓN
            </div>
            <div className="font-space font-bold text-lg leading-tight mb-2" data-id="element-834">
              QR al brief, no a la home.
            </div>
            <div className="text-[11px] opacity-80 leading-relaxed" data-id="element-835">
              Cada flyer apunta a un QR específico que abre directamente la
              página de reserva de brief (30 min, gratis). El embudo más corto
              posible entre el papel y la llamada.
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}